import {
  GRADIENT_GEOMETRY,
  GRADIENT_STOPS,
  MARK_VIEWBOX,
  PATH_INNER,
  PATH_OUTER,
} from '@syncanix/ui';
import { useId } from 'react';

import {
  AURORA_POOLS,
  CONTOUR_WIDTH,
  EDGE_FADE,
  MARK_FIT,
  SPLAT,
  WASH,
  WASH_SPREAD,
  WASH_STROKE,
  WRITE,
} from './hero-mark-tuning';

import './hero-mark.css';

import type { ReactElement } from 'react';

/**
 * The hero's ground — the brand mark as a LIGHT SOURCE behind the headline.
 *
 * ## Why the plain watermark could not be rescued
 *
 * The first port of the design bundle put the whole vector behind the type,
 * filled with the brand gradient at ~10% opacity under a `closest-side` radial
 * mask. On a white page that composites to a pale blue-grey haze: the ramp's
 * cyan, blue and violet all land within a couple of percent of each other once
 * they are 90% white, so the gradient stops reading as colour at all, and the
 * round mask erases the blade silhouette. What was left was a soft circular
 * smear — too faint to be seen on purpose, too broad to be ignored, and sitting
 * over the lead and the CTAs where it cost contrast for nothing.
 *
 * Strength is not the fix. Raised, the smear gets bigger and starts eating the
 * headline; masked harder it becomes a bubble. The problem is that a
 * low-opacity FILL has no edge, and an edgeless shape is a smudge at every
 * opacity. So the mark is built from four layers that each do ONE job, and the
 * one that makes it read as deliberate is the sharp one.
 *
 * 1. **Aurora** — broad ramp pools. The COLOUR (`AURORA_POOLS`).
 * 2. **Bloom** — the blades, filled and blurred, revealed behind the pen by the
 *    wash mask. The MASS, and the colour bleeding in.
 * 3. **Contour** — the blades as a sharp hairline. The EDGE: a crisp line at
 *    low opacity reads as drawn, where a soft fill at low opacity reads as dirt.
 * 4. **Aperture** — the page colour feathered back over the text column, so the
 *    light blooms AROUND the sentence rather than through it. That is what lets
 *    the three layers above carry real colour and still gain contrast; it
 *    carries a breakpoint, so it lives in `hero-mark.css`.
 *
 * Every measured number is in `hero-mark-tuning.ts`; the contrast this all has
 * to clear is gated by
 * `scripts/a11y/hero-ground-contrast.mjs`, which samples real pixels because no
 * other gate in the repo can see a ground made of sibling layers.
 *
 * ## Why the vector comes from `@syncanix/ui`
 *
 * These are the same two blades `Logo` and `BrandLoader` draw, so they are
 * imported rather than transcribed. `logo-paths.ts` exists precisely so the
 * silhouette cannot drift between the static and animated mark.
 *
 * ## Decoration, and the LCP
 *
 * `aria-hidden`: the mark is the wordmark's twin and the page already names
 * itself in the nav and the `h1`. It renders inline with the document — no
 * image request, no deferred chunk — so it costs the hero's LCP nothing beyond
 * its own markup.
 *
 * The design also offers an infinite `sheen` sweep. It is deliberately NOT
 * ported: an `infinite` animation in the hero is the exact shape of the
 * offscreen-driver problem the landing's visibility gate exists to solve, and a
 * one-shot entrance has no business still running a minute later.
 *
 * ## Why there is no nib
 *
 * The mark used to carry a fifth layer: a bright comet running ahead of the
 * contour, meant to read as the pen laying the line down. It did not. Both
 * blades are CLOSED SILHOUETTES rather than centre lines, so the head does not
 * travel along a stroke — it laps the outline of a shape, and a bright point
 * orbiting a logo reads as a loading spinner, or as an artifact nobody can
 * explain. Worse, it had to leave: it faded out on its own timer once it landed,
 * so the most conspicuous thing on the hero appeared for three seconds and then
 * vanished, which is precisely the "motion you notice and cannot identify" that
 * `WRITE`'s own tuning note sets out to avoid.
 *
 * The contour growing along the blade already IS the draw-on. It has a leading
 * edge by construction, it needs nothing to point at it, and it leaves nothing
 * behind to take away.
 */
export function HeroMark(): ReactElement {
  // Unique per instance: two gradients with one id on a page make the second
  // `url(#…)` resolve to the first. `BrandLoader` does the same for the reason.
  const uid = useId().replace(/:/g, '');
  const brand = `hero-mark-brand-${uid}`;
  const outer = `hero-mark-outer-${uid}`;
  const inner = `hero-mark-inner-${uid}`;
  const wash = `hero-mark-wash-${uid}`;

  return (
    <div
      data-sx-mark
      aria-hidden="true"
      className={[
        // Centred on the hero block it is placed in, so it tracks the type
        // instead of a hand-tuned offset that drifts with every copy change.
        'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
        'h-[620px] w-[620px] sm:h-[880px] sm:w-[880px] lg:h-[1080px] lg:w-[1080px]',
      ].join(' ')}
      style={{
        maskImage: EDGE_FADE,
        WebkitMaskImage: EDGE_FADE,
        maskComposite: 'intersect',
        WebkitMaskComposite: 'source-in',
      }}
    >
      {/* 1 — AURORA. The colour, arriving as three drops rather than as one
          fade. Each pool is its own element so it can land on its own beat and
          scale about its OWN centre; see `AURORA_POOLS` for why one shared fade
          was the thing that made the colour look like it was merely turning up.
          `both`, so a pool is invisible rather than parked at full strength
          during its delay. */}
      {AURORA_POOLS.map((pool) => (
        <div
          key={pool.origin}
          className="absolute inset-[-14%]"
          style={{
            background: pool.background,
            transformOrigin: pool.origin,
            filter: 'blur(18px)',
            animation: `sxMarkSplat ${SPLAT} ${pool.delay} both`,
          }}
        />
      ))}

      <svg
        viewBox={MARK_VIEWBOX}
        className="absolute inset-0 block h-full w-full"
        role="presentation"
      >
        <defs>
          <linearGradient id={brand} {...GRADIENT_GEOMETRY} gradientUnits="userSpaceOnUse">
            {GRADIENT_STOPS.map((stop) => (
              <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
            ))}
          </linearGradient>
          {/* `pathLength="1"` is what lets every dash offset below be a fraction
              of the blade rather than a measured user-space length. */}
          <path id={outer} pathLength={1} d={PATH_OUTER} />
          <path id={inner} pathLength={1} d={PATH_INNER} />

          {/* The wash mask — what makes the colour arrive behind the drawing
              front rather than all at once. A very wide stroke on the same two blades, drawn
              with the same `sxMarkWrite` growth, blurred so its leading edge is
              a soft front rather than a wipe. Wide enough (300 units) that the
              stroke straddling each blade's OUTLINE covers that blade's
              interior — the paths are closed silhouettes, not centre lines, so
              a narrow mask would reveal a rim and leave the middle empty.

              `maskUnits="userSpaceOnUse"` with an explicit region: the default
              is a bounding box inflated by 10%, which clips a 60-unit blur and
              puts a straight edge back into the one layer that must not have
              one. It is placed INSIDE the fitted group so its coordinates share
              the transform the blades are drawn under. */}
          <mask id={wash} maskUnits="userSpaceOnUse" x={-400} y={-400} width={2054} height={2054}>
            <g
              fill="none"
              stroke="#fff"
              strokeWidth={WASH_STROKE}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ filter: 'blur(60px)' }}
            >
              <use
                href={`#${outer}`}
                style={{
                  strokeDasharray: 1,
                  strokeDashoffset: 1,
                  animation: `sxMarkWrite ${WASH}, sxMarkWashSpread ${WASH_SPREAD}`,
                }}
              />
              <use
                href={`#${inner}`}
                style={{
                  strokeDasharray: 1,
                  strokeDashoffset: 1,
                  animation: `sxMarkWrite ${WASH}, sxMarkWashSpread ${WASH_SPREAD}`,
                }}
              />
            </g>
          </mask>
        </defs>

        {/* Everything drawn sits inside the fit — see MARK_FIT. `defs` stays in
            raw user space, since a `use` inherits the transform from where it
            is PLACED, not from where its path was defined. */}
        <g transform={MARK_FIT}>
          {/* 2 — BLOOM. The mass, and the colour BLEEDING IN BEHIND THE PEN.
              It used to fade up as a whole shape on a delay, which is what made
              the colour "start weird": the mark was being written along one
              path while the colour arrived everywhere at once on another,
              so the two motions had nothing to do with each other and the fade
              read as a flash. It is now revealed through `#${wash}` — a wide,
              soft stroke that grows along the same blade on the same clock, one
              beat behind. The colour therefore follows the leading edge, which
              is what "self-building" is supposed to look like.

              Blur is in USER UNITS (the viewBox is 1254 wide, so 34 is ~2.7% of
              the box), so it scales with the element instead of being a pixel
              radius that reads differently at every breakpoint. */}
          <g
            mask={`url(#${wash})`}
            style={{
              filter: 'blur(34px)',
              opacity: 0,
              animation: 'sxMarkBloomIn .55s cubic-bezier(.2,.9,.3,1) .3s forwards',
            }}
          >
            <use href={`#${outer}`} fill={`url(#${brand})`} />
            <use href={`#${inner}`} fill={`url(#${brand})`} />
          </g>

          {/* 3 — CONTOUR. The edge, and the layer that writes itself on. Thin
              and SHARP: this is the difference between a drawn mark and a
              smudge, so it takes no blur at any point.

              Its opacity is a flat value rather than an animation. The reveal
              is already carried by the dash growing along the blade, so fading
              the group in on top of that would dim the beginning of the stroke
              exactly while it is being drawn — and the keyframe that used to be
              here animated 0.26 to 0.26, which changed nothing except to
              override the value written beside it. */}
          <g fill="none" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.34 }}>
            <use
              href={`#${outer}`}
              stroke={`url(#${brand})`}
              strokeWidth={CONTOUR_WIDTH}
              style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: `sxMarkWrite ${WRITE}` }}
            />
            <use
              href={`#${inner}`}
              stroke={`url(#${brand})`}
              strokeWidth={CONTOUR_WIDTH}
              style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: `sxMarkWrite ${WRITE}` }}
            />
          </g>
        </g>
      </svg>

      {/* 4 — APERTURE. See `[data-sx-aperture]` in `hero-mark.css`: it is the
          page colour feathered over the text column, weighted toward the small
          grey type that actually has a contrast budget. It carries a breakpoint
          — the copy sits at a completely different height within the mark on a
          390px screen — so its geometry lives in the stylesheet rather than
          here, where a media query cannot reach. */}
      <div data-sx-aperture />
    </div>
  );
}
